// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { getFaceDuplicatePrevention, updateFaceDuplicatePrevention } from '@api/portalServices';
import type {
  FaceDuplicatePreventionGetResponse,
  FaceDuplicatePreventionUpdateRequest,
} from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { ErrorCode } from '@errorHandler/constants';
import { useEmitOnError } from '@errorHandler/context/useEmitOnError';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const MISSING_TENANT: ApiError = {
  code: ErrorCode.INVALID_TENANT_ID,
  message: 'no tenant in session for face duplicate prevention',
};

export const useFaceDuplicatePreventionQuery = (
  options?: Partial<UseQueryOptions<FaceDuplicatePreventionGetResponse, ApiError>>,
) => {
  const { tenantId } = useSession();

  const query = useQuery<FaceDuplicatePreventionGetResponse, ApiError>({
    queryKey: [QueryKey.FACE_DUPLICATE_PREVENTION, tenantId],
    queryFn: () => (tenantId ? getFaceDuplicatePrevention() : Promise.reject(MISSING_TENANT)),
    retry: false,
    staleTime: 0,
    ...options,
  });

  useEmitOnError(query.error);

  return query;
};

export const useFaceDuplicatePreventionMutation = () => {
  const queryClient = useQueryClient();
  const { tenantId } = useSession();

  return useMutation<void, ApiError, FaceDuplicatePreventionUpdateRequest>({
    mutationFn: async (data: FaceDuplicatePreventionUpdateRequest) => {
      await updateFaceDuplicatePrevention(data);
    },
    // Returned so the mutation stays pending until the new value has been read back.
    onSuccess: () =>
      queryClient.invalidateQueries(
        { queryKey: [QueryKey.FACE_DUPLICATE_PREVENTION, tenantId] },
        { throwOnError: true },
      ),
    retry: false,
  });
};
